import { useState } from 'react';
import { motion } from 'framer-motion';
import { FiShoppingCart, FiEye } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import Button from '@components/common/Button';

/**
 * ProductCard Component
 * Props:
 * - image
 * - title
 * - price
 * - stockStatus (in-stock, low-stock, out-of-stock)
 * - variants [{name, options}]
 * - onAddToCart
 * - onAddInstallation
 * Features:
 * - Hover lift with shadow
 * - Variant selector dropdown
 * - Add-to-cart + installation toggle
 * - Loose coupling: actions via props
 */

const ProductCard = ({
  id,
  image,
  title,
  price,
  stockStatus = 'in-stock',
  variants = [],
  onAddToCart,
}) => {
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [includeInstallation, setIncludeInstallation] = useState(false);

  const stockStatusConfig = {
    'in-stock': { text: 'In Stock', color: 'text-green-600', bg: 'bg-green-100' },
    'low-stock': { text: 'Low Stock', color: 'text-orange-600', bg: 'bg-orange-100' },
    'out-of-stock': { text: 'Out of Stock', color: 'text-red-600', bg: 'bg-red-100' },
  };

  const status = stockStatusConfig[stockStatus] || stockStatusConfig['in-stock'];

  const handleAddToCart = () => {
    onAddToCart({
      id,
      title,
      price,
      image,
      variant: selectedVariant,
      includeInstallation,
    });
  };

  return (
    <motion.div
      whileHover={{ y: -8 }}
      transition={{ duration: 0.3 }}
      className="bg-white rounded-lg shadow-md overflow-hidden group hover:shadow-xl transition-shadow duration-300"
    >
      {/* Image Container */}
      <Link to={`/products/${id}`} className="relative block overflow-hidden h-64">
        <img
          src={image}
          alt={title}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
        
        {/* Stock Status Badge */}
        <div className={`absolute top-3 right-3 px-3 py-1 rounded-full text-xs font-semibold ${status.bg} ${status.color}`}>
          {status.text}
        </div>

        {/* Quick View Overlay */}
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
          <div className="flex space-x-2">
            <button className="p-3 bg-white rounded-full hover:bg-secondary-400 hover:text-white transition-colors duration-300">
              <FiEye className="text-xl" />
            </button>
          </div>
        </div>
      </Link>

      {/* Content */}
      <div className="p-4">
        <Link to={`/products/${id}`}>
          <h3 className="text-lg font-semibold text-gray-900 mb-2 hover:text-secondary-500 transition-colors duration-300 line-clamp-2">
            {title}
          </h3>
        </Link>

        <p className="text-2xl font-bold text-secondary-600 mb-4">
          £{price.toFixed(2)}
        </p>

        {/* Variants Selector */}
        {variants.length > 0 && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {variants[0].name}
            </label>
            <select
              value={selectedVariant || ''}
              onChange={(e) => setSelectedVariant(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary-400 text-sm"
            >
              <option value="">Select {variants[0].name}</option>
              {variants[0].options.map((option, index) => (
                <option key={index} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Installation Toggle */}
        <div className="mb-4 flex items-center">
          <input
            type="checkbox"
            id={`installation-${id}`}
            checked={includeInstallation}
            onChange={(e) => setIncludeInstallation(e.target.checked)}
            className="h-4 w-4 text-secondary-500 focus:ring-secondary-400 border-gray-300 rounded"
          />
          <label htmlFor={`installation-${id}`} className="ml-2 text-sm text-gray-700">
            Add Professional Installation (+£50)
          </label>
        </div>

        {/* Add to Cart Button */}
        <Button
          text="Add to Cart"
          type="primary"
          onClick={handleAddToCart}
          disabled={stockStatus === 'out-of-stock'}
          className="w-full"
          icon={<FiShoppingCart />}
        />
      </div>
    </motion.div>
  );
};

export default ProductCard;

